import { useHistory } from 'react-router-dom'
import useToast from 'utils/hooks/use-toast'
import { PublicClientApplication } from '@azure/msal-browser'
import { useGoogleLogin } from '@react-oauth/google'
import { setCookieOnDomain } from 'utils/cookies'
import { ARVORE_ROOT_PATH, MICROSOFT_CLIENT_ID } from 'config/vars'
import { ROOT_PATH } from 'routes/paths'
import {
  DeviceType,
  Provider,
  useAuthenticateWithProviderMutation
} from '__gql-generated__/types'
import { GOOGLE_SCOPES } from './constants'
import { getQueryStringValue } from 'utils/url'

const useExternalLogin = () => {
  const [authenticateWithProvider] = useAuthenticateWithProviderMutation()
  const history = useHistory()
  const { toastMessage } = useToast()

  const handleSuccessfulAuth = (token: string) => {
    setCookieOnDomain('access_token', token, 30)
    history.push(ROOT_PATH)
  }

  const isWebView = Boolean(
    typeof window !== 'undefined' && window.ReactNativeWebView
  )

  let msalInstance: PublicClientApplication | null = null
  if (!isWebView) {
    msalInstance = new PublicClientApplication({
      auth: {
        clientId: MICROSOFT_CLIENT_ID,
        authority: 'https://login.microsoftonline.com/common',
        redirectUri: window.location.origin
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: true
      }
    })
  }

  const googlePopupLogin = useGoogleLogin({
    onSuccess: async tokenResponse => {
      try {
        const { data } = await authenticateWithProvider({
          variables: {
            deviceId: navigator.userAgent,
            deviceType: DeviceType.Browser,
            provider: Provider.Google,
            token: tokenResponse.access_token
          }
        })

        if (data?.authenticateWithProvider) {
          const token = data.authenticateWithProvider
          handleSuccessfulAuth(token)
        }
      } catch (error) {
        toastMessage({
          position: 'bottom-left',
          message: 'Erro ao realizar login com o Google',
          type: 'error'
        })
      }
    },
    onError: () => {
      toastMessage({
        position: 'bottom-left',
        message: 'Login cancelado',
        type: 'error'
      })
    },
    scope: GOOGLE_SCOPES,
    flow: 'implicit'
  })

  const loginWithGoogle = isWebView
    ? () => {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ type: 'open_google_login' })
        )
      }
    : googlePopupLogin

  const microsoftPopupLogin = async () => {
    if (!msalInstance) return

    try {
      await msalInstance.initialize()

      const scopes = ['user.read']

      const response = await msalInstance.loginPopup({ scopes })
      const tokenResponse = await msalInstance.acquireTokenSilent({
        scopes,
        account: response.account
      })

      if (!tokenResponse) {
        toastMessage({
          position: 'bottom-left',
          message: 'Erro ao realizar login com a Microsoft',
          type: 'error'
        })
        return
      }

      const { data } = await authenticateWithProvider({
        variables: {
          deviceId: navigator.userAgent,
          deviceType: DeviceType.Browser,
          provider: Provider.Microsoft,
          token: tokenResponse.accessToken
        }
      })

      if (data?.authenticateWithProvider) {
        const token = data.authenticateWithProvider
        handleSuccessfulAuth(token)
      }
    } catch (error) {
      toastMessage({
        position: 'bottom-left',
        message: 'Erro ao realizar login com a Microsoft',
        type: 'error'
      })
    }
  }

  const loginWithMicrosoft = isWebView
    ? () => {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ type: 'open_microsoft_login' })
        )
      }
    : microsoftPopupLogin

  const loginWithLex = () => {
    const popup = window.open(
      `${ARVORE_ROOT_PATH}/auth/sso/lex`,
      'popup',
      `popup=true,width=420,height=600,left=${
        window.innerWidth / 2 - 210
      },top=${window.innerHeight / 2 - 300}`
    )

    window.addEventListener('message', evt => {
      if (evt.data?.msg === 'auth-sso' && evt.data?.token) {
        popup.close()

        if (getQueryStringValue('redirectTo')) {
          window.location.href = `${ARVORE_ROOT_PATH}/login?redirectTo=${encodeURIComponent(
            getQueryStringValue('redirectTo')
          )}`
        } else {
          window.location.href = `${ARVORE_ROOT_PATH}/login`
        }
      }
    })

    const checkPopup = setInterval(() => {
      if (popup.closed) {
        // user cancel operation
        clearInterval(checkPopup)
      }
    }, 1000)
  }

  return { loginWithGoogle, loginWithMicrosoft, loginWithLex }
}

export default useExternalLogin
